import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import InfoIcon from '@mui/icons-material/Info';
import { BlobType } from '@myme/utils';
import {
  AudioIcon,
  EditIcon,
  LinkIcon,
  MymeLocationBasedIcon,
  OpenAnswerQuestionIcon,
  PhotoIcon,
  TextIcon,
  VideoIcon
} from 'src/icons';
import { mq } from 'src/themes';
import { usePalette, useTranslation } from 'src/hooks';
import { blobEditorPageRoute } from 'src/routes/route-list';
import { Box, IconButton, SvgIcon, Tooltip, Typography } from 'src/components/atoms';
import { Flex } from 'src/components/layout';
import { useDialog } from 'src/components/organisms';
import { BlobsWithoutSetLocation } from 'src/components/pages/mymes/editor/contentStep/ContentContext';
import { ErrorLocationMessage } from './location/ErrorLocationMessage';
import { MapDialog } from './location/MapDialog';

interface SingleBlobProps {
  blobId: string;
  // it is optional due to that blobs in blobs library don't have module
  moduleId?: string;
  name: string;
  blobType: BlobType;
  showInfo?: boolean;
  locationIcon?: boolean;
  blobsWithoutSetLocation?: BlobsWithoutSetLocation;
}

const blobTypeIcon = {
  AUDIO: <AudioIcon />,
  VIDEO: <VideoIcon />,
  IMAGE: <PhotoIcon fill='#fff' strokeWidth='0' />,
  QUIZ: <OpenAnswerQuestionIcon />,
  LINK: <LinkIcon />,
  TEXT: <TextIcon stroke='none' />
};

const StyledBlob = styled.div(
  ({ theme }) => `
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    max-width: 25rem;
    user-select: none;
    padding: 0.25rem 1rem;
    background-color: ${theme.palette.secondary.main};
    border-radius: 1rem;
    color: ${theme.palette.common.white};
    word-break: break-word;

    ${mq.xl}{
      max-width: 21rem;
    }
  `
);

type LowerCaseBlobType = Lowercase<BlobType>;

export const SingleBlob = ({
  blobId,
  moduleId,
  name,
  blobType,
  showInfo,
  locationIcon,
  blobsWithoutSetLocation
}: SingleBlobProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { primary } = usePalette();
  const mapDialog = useDialog();

  const blobWithoutSetLocation = blobsWithoutSetLocation?.find(
    currentBlobWithError => currentBlobWithError.blobId === blobId && currentBlobWithError.moduleId === moduleId
  );

  return (
    <>
      <StyledBlob data-testid={`Single blob ${blobId}`}>
        <Flex>
          <Box height='1.25rem'>
            <Tooltip title={t(`pages.mymes.editor.blobs.types.${blobType.toLocaleLowerCase() as LowerCaseBlobType}`)}>
              <SvgIcon fontSize='small' sx={{ mr: 1 }}>
                {blobTypeIcon[blobType]}
              </SvgIcon>
            </Tooltip>
          </Box>
          <Typography>{name}</Typography>
        </Flex>

        <Flex>
          {showInfo && (
            <Tooltip
              title={<span style={{ whiteSpace: 'pre-line' }}>{t('pages.mymes.editor.blobs.time_interval_info')}</span>}
            >
              <IconButton>
                <InfoIcon />
              </IconButton>
            </Tooltip>
          )}
          {locationIcon && (
            <>
              <IconButton onClick={mapDialog.openDialog}>
                <SvgIcon sx={{ height: '1.25rem', width: '1.25rem' }}>
                  <MymeLocationBasedIcon fill={blobWithoutSetLocation ? '#FF5C60' : 'inherit'} />
                </SvgIcon>
              </IconButton>
              {mapDialog.isOpen && (
                <MapDialog
                  closeDialog={mapDialog.closeDialog}
                  moduleId={moduleId}
                  blobId={blobId}
                  onClose={mapDialog.closeDialog}
                />
              )}
            </>
          )}
          <IconButton onClick={() => navigate(blobEditorPageRoute(blobId))}>
            <SvgIcon fontSize='small' color='primary'>
              <EditIcon fill={primary.main} />
            </SvgIcon>
          </IconButton>
        </Flex>
      </StyledBlob>
      {blobWithoutSetLocation && <ErrorLocationMessage />}
    </>
  );
};
